import { config } from './constants';

export type CustomPageListItem = {
  id?: string;
  title?: string;
  path?: string;
};

type ListedFile = {
  fileId?: string;
  fileName?: string;
};

export type CustomPageMetadata = {
  title?: string;
  iframeUrl?: string;
};

export const CUSTOM_PAGE_PATHS = ['/slovak', '/maths', '/misc'];
export const CUSTOM_PAGE_FOLDER_TYPE = 'publicFolder';

export const normalizeCustomPagePath = (path: string): string => {
  const trimmed = path.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
};

export const customPageFolder = (path: string): string => {
  const normalized = normalizeCustomPagePath(path);
  const folder = normalized.replace(/^\/+/, '').replace(/\//g, '-');
  const base = folder.length > 0 ? folder : 'root';
  return 'custom-' + base;
};

export const customPageMetadataFileName = (pageId: string): string => {
  return pageId + '.metadata.json';
};

export const customPageHtmlFileName = (pageId: string): string => {
  return pageId + '.html';
};

export const customPageDownloadUrl = (path: string, fileName: string): string => {
  const folder = customPageFolder(path);
  return (
    config.API_URL +
    '/downloadData/' +
    CUSTOM_PAGE_FOLDER_TYPE +
    '/' +
    encodeURIComponent(folder) +
    '/' +
    encodeURIComponent(fileName)
  );
};

const listFolderFiles = (path: string): Promise<ListedFile[]> => {
  const folder = customPageFolder(path);
  return fetch(
    config.API_URL +
      '/list/' +
      CUSTOM_PAGE_FOLDER_TYPE +
      '/' +
      encodeURIComponent(folder),
    { credentials: 'include' }
  )
    .then((res: Response) => {
      if (!res.ok) {
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      return res.json();
    })
    .then((data: ListedFile[]) => (Array.isArray(data) ? data : []));
};

const downloadCustomPageMetadataByFile = (
  path: string,
  fileName: string
): Promise<CustomPageMetadata> => {
  return fetch(customPageDownloadUrl(path, fileName), { credentials: 'include' })
    .then((res: Response) => {
      if (!res.ok) {
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      return res.json();
    })
    .then((data: CustomPageMetadata) => data || {});
};

export const downloadCustomPageMetadata = (
  path: string,
  pageId: string
): Promise<CustomPageMetadata> => {
  return downloadCustomPageMetadataByFile(path, customPageMetadataFileName(pageId));
};

export const downloadCustomPageHtml = (
  path: string,
  pageId: string
): Promise<string> => {
  return fetch(customPageDownloadUrl(path, customPageHtmlFileName(pageId)), {
    credentials: 'include',
  }).then((res: Response) => {
    if (!res.ok) {
      throw new Error(`Server error: ${res.status} ${res.statusText}`);
    }
    return res.text();
  });
};

export const listCustomPages = async (path: string): Promise<CustomPageListItem[]> => {
  const files = await listFolderFiles(path);
  const metadataFiles = files
    .map((file) => file.fileName ?? file.fileId)
    .filter((fileName): fileName is string => Boolean(fileName))
    .filter((fileName) => fileName.endsWith('.metadata.json'));

  const metadataList = await Promise.all(
    metadataFiles.map(async (fileName) => {
      try {
        const metadata = await downloadCustomPageMetadataByFile(path, fileName);
        const pageId = fileName.replace(/\.metadata\.json$/, '');
        return {
          id: pageId,
          title: metadata.title ?? 'Stránka',
          path,
        } as CustomPageListItem;
      } catch (error) {
        return null;
      }
    })
  );

  return metadataList.filter((item): item is CustomPageListItem => Boolean(item));
};

export const findCustomPageMetadata = async (
  pageId: string,
  preferredPath?: string
): Promise<{ path: string; metadata: CustomPageMetadata }> => {
  const normalizedPreferred = preferredPath ? normalizeCustomPagePath(preferredPath) : '';
  const pathsToCheck = [
    ...(normalizedPreferred ? [normalizedPreferred] : []),
    ...CUSTOM_PAGE_PATHS.filter((path) => path !== normalizedPreferred),
  ];

  for (const path of pathsToCheck) {
    try {
      const metadata = await downloadCustomPageMetadata(path, pageId);
      return { path, metadata };
    } catch (_error) {
      // try next
    }
  }

  throw new Error('Custom page not found');
};

export const uploadCustomPageFile = (path: string, file: File): Promise<void> => {
  const folder = customPageFolder(path);
  const formData = new FormData();
  formData.append('folderType', CUSTOM_PAGE_FOLDER_TYPE);
  formData.append('folder', folder);
  formData.append('file', file);

  return fetch(config.API_URL + '/uploadData', {
    credentials: 'include',
    method: 'POST',
    body: formData,
  }).then((res: Response) => {
    if (!res.ok) {
      throw new Error(`Server error: ${res.status} ${res.statusText}`);
    }
  });
};

export const deleteCustomPageFile = (path: string, fileName: string): Promise<void> => {
  const folder = customPageFolder(path);
  return fetch(
    config.API_URL +
      '/deleteFile/' +
      CUSTOM_PAGE_FOLDER_TYPE +
      '/' +
      encodeURIComponent(folder) +
      '/' +
      encodeURIComponent(fileName),
    { credentials: 'include' }
  ).then((res: Response) => {
    if (!res.ok) {
      throw new Error(`Server error: ${res.status} ${res.statusText}`);
    }
  });
};
